import { Button, Empty } from "antd";
import useUserPermissions from "config/UserPermissionsContext/useUserPermissions";
import useThemes from "hooks/useThemes";
import PageContainer from "layout/PageContainer";
import useTemplateCounts from "pages/Themes/useTemplateCounts";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "store";
import { updateBusinessDetails } from "store/business/businessActions";
import SelectedThemesGrid from "./SelectedThemesGrid";
import ThemeSelectionModal from "./ThemeSelectionModal";
import ThemesLoadingSkeleton from "./ThemesLoadingSkeleton";

export const THEMES_PAGE_ID = "themes";

const ThemesPage = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = useUserPermissions();
  const canEditBusiness = hasPermission("business_details_write");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { themes: allThemes = [], isLoading: allThemesLoading } = useThemes();
  const currentBusiness = useAppSelector((state) => state.user.currentBusiness);
  const [selectedThemes, setSelectedThemes] = useState<string[]>(
    currentBusiness?.brand?.themes || []
  );

  const saveBusinessThemes = async (themesToUpdate: string[]) => {
    if (!currentBusiness) {
      console.error("Cannot save themes: current business not found.");
      return;
    }
    await dispatch(
      updateBusinessDetails({ themes: themesToUpdate } as any)
    ).unwrap();
  };

  const handleRemoveTheme = async (themeName: string) => {
    const updatedThemes = selectedThemes.filter((t) => t !== themeName);
    setSelectedThemes(updatedThemes);
    await saveBusinessThemes(updatedThemes);
  };

  const {
    templateCounts,
    isLoading: isLoadingCounts,
    error: templateCountsError,
  } = useTemplateCounts();

  useEffect(() => {
    if (templateCountsError) {
      console.error("Template Counts Error:", templateCountsError);
    }
  }, [templateCountsError]);

  const handleModalSave = async () => {
    await saveBusinessThemes(selectedThemes);
    setIsModalOpen(false);
  };

  return (
    <PageContainer
      title="Themes"
      subtitle="Themes provide the templates for your post designs."
      headerActions={
        canEditBusiness && (
          <Button
            onClick={() => setIsModalOpen(true)}
            disabled={!allThemes.length || allThemesLoading}
            loading={allThemesLoading}
            className="max-w-xs"
          >
            Edit Themes
          </Button>
        )
      }
    >
      <div id={THEMES_PAGE_ID} className="flex flex-col h-full">
        <main className="flex-grow">
          {allThemesLoading ? (
            <ThemesLoadingSkeleton />
          ) : !allThemes.length ? (
            <Empty className="p-20" description="No themes available." />
          ) : selectedThemes.length > 0 ? (
            <SelectedThemesGrid
              themes={allThemes}
              selectedThemes={selectedThemes}
              templateCounts={templateCounts}
              isLoadingCounts={isLoadingCounts}
              onAddThemeClick={() => setIsModalOpen(true)}
              onRemove={handleRemoveTheme}
            />
          ) : (
            <Empty className="p-20" description="No themes selected" />
          )}
        </main>
      </div>

      <ThemeSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedThemes={selectedThemes}
        setSelectedThemes={setSelectedThemes}
        allThemes={allThemes}
        allThemesLoading={allThemesLoading}
        onSave={handleModalSave}
        templateCounts={templateCounts}
        isLoadingCounts={isLoadingCounts}
      />
    </PageContainer>
  );
};

export default ThemesPage;
